import { useEffect, useState } from "react";
import { PAGINATION } from "@/lib/constants";
import { useSearchParams } from "react-router-dom";

const debounceTime = 2000;

export function useSearchPagination() {
  const [params, setParams] = useSearchParams();

  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState<string | null>(null);

  const [pageSelected, setPageSelected] = useState<number>(
    PAGINATION.DEFAULT_PAGE_NUMBER
  );

  const [rowsPerPageValue, setRowsPerPageValue] = useState<number>(
    PAGINATION.DEFAULT_ROWS_PER_PAGE
  );

  const reset = () => {
    setSearch(null);
    setDebouncedSearch(null);
    setPageSelected(PAGINATION.DEFAULT_PAGE_NUMBER);
    setRowsPerPageValue(PAGINATION.DEFAULT_ROWS_PER_PAGE);

    const newParams = new URLSearchParams();
    newParams.set("pageNo", PAGINATION.DEFAULT_PAGE_NUMBER.toString());
    setParams(newParams);
  };

  useEffect(() => {
    const newParams = new URLSearchParams();
    newParams.set("pageNo", PAGINATION.DEFAULT_PAGE_NUMBER.toString());
    setParams(newParams);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== null) {
        setDebouncedSearch(search);
        setPageSelected(1);
      }
    }, debounceTime);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setParams((prev) => {
      const newParams = new URLSearchParams(prev);

      newParams.set("pageNo", pageSelected.toString());

      if (debouncedSearch && debouncedSearch.trim() !== "") {
        newParams.set("search", debouncedSearch);
      } else {
        newParams.delete("search");
      }

      return newParams;
    });
  }, [pageSelected, debouncedSearch, setParams]);

  return {
    debouncedSearch,
    search: search ?? debouncedSearch ?? "",
    setSearch,
    pageSelected,
    setPageSelected,
    rowsPerPageValue,
    setRowsPerPageValue,
    reset,
  };
}